import { memo, useEffect, useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  type SelectProps,
  SelectTrigger,
  type SelectTriggerProps,
} from '@air/primitive-select';
import { compareFontVariants, formatVariantLabel, getDefaultFontVariant } from '@air/utils-fonts';

import { useFontMetadata } from '~/components/v2/hooks/queries/useFontMetadata';
import { useFontsList } from '~/components/v2/hooks/queries/useFontsList';
import { type Theme } from '~/components/v2/store/shared/createThemeSlice';

export type BuiltInFontVariantSelectProps = Pick<SelectProps, 'onValueChange' | 'value'> &
  Pick<SelectTriggerProps, 'aria-describedby' | 'aria-invalid' | 'aria-label' | 'id'> & {
    /** Trigger classes, for surfaces whose own controls use a different type scale. */
    className?: string;
    family: string;
    placeholder?: string;
    /** The list portals to the body, so surfaces on a themed subtree (e.g. the asset modal) pass theirs. */
    'data-theme'?: Theme;
  };

export const BuiltInFontVariantSelect = memo(
  ({
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-label': ariaLabel,
    className,
    'data-theme': dataTheme,
    family,
    id,
    onValueChange,
    value,
    placeholder = 'Choose a variant…',
  }: BuiltInFontVariantSelectProps) => {
    const { data: fonts } = useFontsList();
    const { data: fontMetadata, isLoading } = useFontMetadata(
      {
        metadataUrl: fonts?.data?.find((f) => f.family === family)?.metadataUrl,
      },
      {
        enabled: !!family,
      },
    );

    const sortedVariants = useMemo(() => {
      if (!fontMetadata?.variants) {
        return [];
      }

      return [...fontMetadata.variants]
        .map((variant) => {
          return {
            label: formatVariantLabel(variant),
            value: variant,
          };
        })
        .sort((a, b) => compareFontVariants(a.value, b.value));
    }, [fontMetadata?.variants]);

    useEffect(() => {
      const variants = fontMetadata?.variants;

      if (!variants?.length || !onValueChange) {
        return;
      }

      const isCurrentValueValid = value !== undefined && value !== '' && variants.includes(value);

      if (!isCurrentValueValid) {
        const defaultVariant = getDefaultFontVariant(variants);

        if (defaultVariant !== undefined) {
          onValueChange(defaultVariant);
        }
      }
    }, [fontMetadata?.variants, onValueChange, value]);

    if (!family) {
      return (
        <Select disabled value="">
          <SelectTrigger
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-label={ariaLabel}
            className={className}
            disabled
            id={id}
            placeholder="Choose a font first…"
          />
        </Select>
      );
    }

    return (
      // `?? ''` keeps the Select CONTROLLED through the moment a family change clears the variant
      // (ShareDesignFontRow deliberately emits `variant: undefined` until the default lands) — an
      // undefined value would flip Radix to uncontrolled and back, which React warns about and
      // which can strand a stale selection.
      <Select disabled={isLoading} onValueChange={onValueChange} value={value ?? ''}>
        <SelectTrigger
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-label={ariaLabel}
          className={className}
          id={id}
          placeholder={placeholder}
        />
        <SelectContent data-theme={dataTheme}>
          {sortedVariants.length > 0 ? (
            sortedVariants.map((variant) => (
              <SelectItem
                data-font-variant={variant.value}
                data-value={variant.value}
                key={variant.value}
                value={variant.value}
              >
                {variant.label}
              </SelectItem>
            ))
          ) : (
            <SelectItem data-font-variant="regular" data-value="regular" value="regular">
              Regular
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    );
  },
);

BuiltInFontVariantSelect.displayName = 'BuiltInFontVariantSelect';
